import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FilterCriteria } from "tasks-experience-client";
import { IdValuePairDTO, WorkflowState } from "common-util-experience-client";
import { EligibilityWorkflowStateConstants, WorkflowConstants } from "../../helper/WorkflowConstants";
import { GridWorkflowStatusDropdown } from "../../grid/GridWorkflowStatusDropdown";
import { GridDropdown } from "../../grid/GridDropdown";
import { createLocationApi, createWorkflowApi } from "../../api-helpers/CommonUtilsApiHelper";
import { createEligiblesApi } from "../../api-helpers/PatientApiHelper";
import { Constants } from "../../constants/Lookups";
import OffCanvas from "../../components/offCanvas";
import { AddEligiblePatient } from "./AddEligiblePatient";
import Toaster, { ToastConfig } from "../../components/Toaster";
import { ErrorTypes } from "../../interfaces/errors";
import { UsersDto } from "users-experience-client";
import { UserContextHolder } from "../../auth/UserContextHolder";
import { GridUsersDropdown } from "../../grid/GridUsersDropdown";
import { useRoleAuthorization } from "../../customHooks/useRoleAuthorization";
import { USER_ROLES } from "../../constants/UserRoles";

interface EligibilityHeaderProps {
  patientId?: string;
  onUpdateFilters: (filterArray: FilterCriteria[]) => void;
  reloadGrid: () => void
  breadcrumbs: boolean | undefined;
}

export const EligibilityHeader = (props: EligibilityHeaderProps) => {
  const [searchText, setSearchText] = useState<string>();
  const [statuses, setStatuses] = useState<WorkflowState[]>([]);
  const [initialStatusIds, setInitialStatusIds] = useState<string[]>([]);
  const [assignees, setAssignees] = useState <UsersDto[] | null>();
  const [eligibilityStatuses, setEligibilityStatuses] = useState<WorkflowState[]>([]);
  const [numberOfAttempts, setNumberOfAttempts] = useState<IdValuePairDTO[] | null>();
  const [pcp, setPcp] = useState<IdValuePairDTO[] | null>();
  const [state, setState] = useState<IdValuePairDTO[] | null>();
  const [offCanvasShow, setOffCanvasShow] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [currentUser, setCurrentUser] = useState<UsersDto>();
  const [toastConfig, setToastConfig] = useState<ToastConfig>({
    type: "success" as ErrorTypes,
    title: "",
    icon: "",
  });
  const isSupportRole = useRoleAuthorization([USER_ROLES.SUPPORT]);

  const locationApi = createLocationApi();
  const eligiblesApi = createEligiblesApi();
  const WorkflowApi = createWorkflowApi();
  

  const maxNumberOfAttempts = 10;
  const getNumberOfAttempts = async () => {
    const response = Array.from({length: maxNumberOfAttempts + 1}, (_, i) => i.toString());
    const mappedResponse = response.map((item) => ({
      id: item,
      value: item,
    }));
    return mappedResponse;
  };

  const getAllStates = async () => {
    try {
      const { data } = await locationApi.locationControllerFindAllStates();
      return data;
    } catch (error) {
      console.error('Failed to fetch states');
      return [];
    }
  };

  const getAllPcp = async () => {
    try {
      const { data } = await eligiblesApi.eligiblesControllerGetAllPCPNames();
      const mappedResponse = data.map((item) => ({
        id: item,
        value: item,
      }));
      return mappedResponse;
    } catch (error) {
      console.error('Failed to fetch PCP names');
      return [];
    }
  };

  const loadEligibilityStatuses = async () => {
    try {
        const { data } = await WorkflowApi.workflowControllerGetStatesForWorkflow(WorkflowConstants.eligibility);
        setEligibilityStatuses(data);
    } catch (error) {
      console.log(error);
    }
  };

  const showToaster = async (toasterConfig: ToastConfig) => {
    setToastConfig(toasterConfig as ToastConfig);
    setShowToast(true);
  };

  const filters: FilterCriteria[] = [];
  const updateFilters = () => {
    if (assignees && assignees.length > 0) {
      const includeNull = assignees.some(assignee => assignee.id === Constants.NOT_SET);
      const filterOutIsNotSetAssignee = assignees
        .filter((assignee) => assignee.id !== Constants.NOT_SET)
        .map((assignee) => assignee.id);
  
      if (filterOutIsNotSetAssignee.length > 0 && includeNull) {
        filters.push({
          propertyName: "assignedUserId",
          operator: 'in-or-null',
          propertyValue: filterOutIsNotSetAssignee
        });
      } else {
          filters.push({
            propertyName: "assignedUserId",
            operator: filterOutIsNotSetAssignee.length ? 'in' : 'is-null',
            propertyValue: filterOutIsNotSetAssignee.length ? filterOutIsNotSetAssignee : null
          });
      }
    }  
    if ((searchText?.length ?? 0) > 0) {
      filters.push({
        propertyName: "name",
        operator: "contains",
        propertyValue: searchText,
      });
      const phoneNumber = searchText?.replace(/[^0-9]/g, '');

      if (phoneNumber && phoneNumber.length > 0){
        filters.push({
          propertyName: "phoneNumber",
          operator: "contains",
          propertyValue: phoneNumber
        });
      }
    }
    if (statuses && statuses.length > 0) {
      const isNullStatus = statuses.some(status => status.stateId === Constants.NULL.toLowerCase());
      const filterOutNullStatus = statuses.filter((status) => status.stateId !== Constants.NULL.toLowerCase());
      
      filters.push({
        propertyName: "currentStatus",
        operator: isNullStatus ? "in-or-null" : "in",
        propertyValue: (isNullStatus ? filterOutNullStatus : statuses)
          .map((p) => p.stateId),
      });
    }
    if (numberOfAttempts && numberOfAttempts.length > 0) {
      filters.push({
        propertyName: "numberOfAttempts",
        operator: "in",
        propertyValue: numberOfAttempts.map((p) => p.id),
      });
    }
    if (pcp && pcp.length > 0) {
      filters.push({
        propertyName: "pcpName",
        operator: "in",
        propertyValue: pcp.map((p) => p.id),
      });
    }
    if (state && state.length > 0) {
      filters.push({
        propertyName: "stateId",
        operator: "in",
        propertyValue: state.map((p) => p.id),
      });
    }

    props.onUpdateFilters(filters);
  };

  useEffect(() => {
    if (currentUser && isSupportRole) {
      setAssignees([
        { id: currentUser.id } as UsersDto,
        { id: Constants.NOT_SET } as UsersDto
      ]);
    }
  }, [currentUser]);

  useEffect(() => {
    loadEligibilityStatuses();

    UserContextHolder.addListener(() => {
      setCurrentUser(UserContextHolder.getUser());
    });     
  }, []);

  useEffect(() => {
    const defaultStatuses = eligibilityStatuses.filter(status => 
      Object.values(EligibilityWorkflowStateConstants).includes(status.stateName)
    );

    setStatuses([...defaultStatuses, { 
      stateId: Constants.NULL.toLowerCase(), 
      stateName: Constants.NULL 
    } as WorkflowState
    ]);
    setInitialStatusIds([...defaultStatuses.map((status) => status.stateId), Constants.NULL.toLowerCase()]);
  }, [eligibilityStatuses]);

  useEffect(updateFilters, [searchText, statuses, numberOfAttempts, pcp, state, assignees]);

  return (
    <>
      <div className="row">
        <Toaster
          show={showToast}
          onClose={() => setShowToast(false)}
          type={toastConfig.type}
          title={toastConfig.title}
          message={toastConfig.message}
          icon={toastConfig.icon}
        />
      </div>
      {props.breadcrumbs && (
        <div
          className="d-flex align-items-center px-3 px-md-4 px-lg-5 py-2 border-bottom"
          aria-label="breadcrumb"
        >
          <div className="flex-fill d-flex align-items-center">
            <div>
              <ol className="breadcrumb">
                <li className="breadcrumb-item breadcrumb-item-first">
                  <Link to="/">
                    <span className="material-symbols-outlined fs-14">
                      home
                    </span>
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Eligible Patients
                </li>
              </ol>
              <h1 className="h3 m-0">Eligible Patients</h1>
            </div>
          </div>
          <div className="justify-content-end d-flex align-items-center">
            <button
              className="btn btn-sm btn-secondary ms-2 px-4 py-md-2 rounded-pill"
              onClick={() => setOffCanvasShow(true)}
            >
              Add Eligible Patient
            </button>
          </div>
        </div>
      )}

      <div className="d-md-flex align-items-center px-3 px-md-4 px-lg-5 py-2 border-bottom">
        <div className="flex-fill d-flex align-items-center">
          <div className="search-box max-w-md-18-75">
            <span className="material-symbols-outlined search-box__icon">
              search
            </span>
            <div className="typeahead-container search-box d-flex">
              <span className="material-symbols-outlined search-box__icon">
                search
              </span>
              <input
                type="text"
                name="searchEligibility"
                className="form-control search-box__input fw-normal fs-12"
                placeholder="Search by Name or Phone"
                onChange={(event) => setSearchText(event.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="d-flex flex-wrap align-items-center">
          <GridUsersDropdown
            label="Assignee"
            onChange={setAssignees}
            initialUserIds={assignees?.map((assignee) => assignee.id) || []}
          />
          <GridWorkflowStatusDropdown
            label="Current Status"
            type={WorkflowConstants.eligibility}
            onChange={setStatuses}
            initialStatusIds={initialStatusIds}
          />

          <GridDropdown
            label="No. of Attempts"
            keyProvider={(item) => item.id}
            labelProvider={(item) => item.value}
            dataProvider={getNumberOfAttempts}
            onChange={setNumberOfAttempts}
          />

          <GridDropdown
            label="PCP"
            keyProvider={(item) => item.id}
            labelProvider={(item) => item.value}
            dataProvider={getAllPcp}
            onChange={setPcp}
          />

          <GridDropdown
            label="State"
            keyProvider={(item) => item.id}
            labelProvider={(item) => item.value}
            dataProvider={getAllStates}
            onChange={setState}
          />
        </div>

        <OffCanvas
          title="Add Eligible Patient"
          offCanvasShow={offCanvasShow}
          setOffCanvasShow={setOffCanvasShow}
          placement="top"
          backdrop="static"
          className="lightbox-offcanvas eligible-form-offcanvas"
        >
          <AddEligiblePatient
            setOffCanvasShow={setOffCanvasShow}
            showToaster={showToaster}
            reloadGrid={props.reloadGrid}
          />
        </OffCanvas>
      </div>
    </>
  );
};
